import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useHeader } from '@/components/context/HeaderContext';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  useGetContentCategoriesQuery,
  useUpdateContentCategoryMutation,
} from '@/store/api/categoryApi';
import { useGetCategoryAnalyticsQuery } from '@/store/api/analyticsApi';
import { z } from 'zod';
import UnLockedTable from '../unlockedtable';
import { getUnlockedCategoryPath } from '@/utils/contentRoutes';
import FullScreenLoader from '@/components/common/FullScreenLoader';
import { NotFoundPage } from '@/pages/NotFoundPage';
const minCharNoSpaces = (min: number) => (val: string) => val.replace(/\s+/g, '').length >= min;
const noMultipleSpaces = (val: string) => !/\s{2,}/.test(val);

const categorySchema = z.object({
  title: z
    .string()
    .min(1, 'Title is required')
    .max(50, 'Max 50 characters')
    .refine(noMultipleSpaces, {
      message: 'Only one space is allowed between words',
    })
    .transform((val) => val.trim())
    .refine(minCharNoSpaces(3), {
      message: 'Title must be at least 3 characters (excluding spaces)',
    }),

  description: z
    .string()
    .min(5, 'Description is required')
    .max(400, 'Max 400 characters')
    .refine(noMultipleSpaces, {
      message: 'Only one space is allowed between words',
    })
    .transform((val) => val.trim())
    .refine(minCharNoSpaces(5), {
      message: 'Description must be at least 5 characters (excluding spaces)',
    }),
});

export default function CategoryDetailsPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const categoryIdNumber = Number(categoryId);
  const [updateCategory, { isLoading }] = useUpdateContentCategoryMutation();
  const { data: categories = [], isLoading: isCategoriesLoading } =
    useGetContentCategoriesQuery(false);
  const { data: categoryAnalytics } = useGetCategoryAnalyticsQuery(categoryIdNumber, {
    skip: Number.isNaN(categoryIdNumber),
  });
  const content = categories.find((item) => String(item.id) === categoryId);
  const { setHeader } = useHeader();
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState(content);

  const handleEditToggle = () => {
    if (isEditing) {
      setForm(content);
      setErrors({});
    }

    setIsEditing((prev) => !prev);
  };

  useEffect(() => {
    if (content) {
      setForm(content);
    }
  }, [content]);

  useEffect(() => {
    if (!content) return;

    setHeader({
      type: 'content',
      props: {
        title: content.title,
        breadcrumbs: [
          { label: 'Home', href: '/' },
          { label: 'Open Content', href: '/open-content' },
          { label: content.title, href: getUnlockedCategoryPath(content.id) },
        ],
        onBack: () => window.history.back(),
        lastUpdated: '14-April-2026, 3:00PM',
      },
    });

    return () => setHeader(null);
  }, [content, setHeader]);

  if (isCategoriesLoading)
    return (
      <div className="p-6">
        <FullScreenLoader />
      </div>
    );
  if (!content)
    return (
      <div>
        <NotFoundPage />
      </div>
    );

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      await updateCategory({
        id: form!.id,
        title: form!.title,
        description: form!.description,
        benefits: form!.benefits,
        isLocked: form!.isLocked,
      }).unwrap();

      setIsEditing(false);
    } catch (error: any) {
      const backendErrors: Record<string, string> = {};

      if (typeof error?.data === 'string') {
        backendErrors.general = error.data;
      }

      if (Array.isArray(error?.data)) {
        error.data.forEach((msg: string, index: number) => {
          backendErrors[`error_${index}`] = msg;
        });
      }

      if (typeof error?.data === 'object' && !Array.isArray(error?.data)) {
        Object.entries(error.data).forEach(([key, value]) => {
          backendErrors[key] = Array.isArray(value) ? value[0] : String(value);
        });
        0;
      }

      setErrors((prev) => ({ ...prev, ...backendErrors }));
    }
  };

  const validateForm = () => {
    const result = categorySchema.safeParse(form);

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as string;
        fieldErrors[field] = err.message;
      });
      setErrors(fieldErrors);
      return false;
    }

    setErrors({});
    return true;
  };
  const isFormValid = categorySchema.safeParse(form).success;
  const totalContent = categoryAnalytics?.totalContent ?? 0;
  const totalPlays = categoryAnalytics?.totalPlays ?? 0;
  const totalUsers = categoryAnalytics?.totalUsers ?? 0;

  return (
    <>
      <div className="w-full space-y-6 font-noto max-w-[1600px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 xl:gap-6 px-3 sm:px-0">
          <div className="bg-white rounded-xl shadow-md   border-[#4B00821F]/12 p-6 lg:col-span-3">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold text-darkPurple">Basic Details</h2>

              {/* <Button variant="outline" onClick={() => setIsEditing(!isEditing)}>
                {isEditing ? 'Cancel' : 'Edit'}
              </Button> */}
              {/* <button
                onClick={handleEditToggle}
                className="flex items-center gap-2 p-2 rounded-md "
              >
                <Edit size={20} />{' '}
                <p className="text-lg text-black">{isEditing ? 'Cancel' : 'Edit'}</p>
              </button> */}
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-[16px] font-semibold text-[#62556B]">Title</label>
                <Input
                  disabled={!isEditing}
                  value={form?.title ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm({ ...form!, title: value });

                    const result = categorySchema.safeParse({
                      ...form,
                      title: value,
                    });

                    if (!result.success) {
                      const fieldError = result.error.errors.find((err) => err.path[0] === 'title');
                      setErrors((prev) => ({
                        ...prev,
                        title: fieldError?.message || '',
                      }));
                    } else {
                      setErrors((prev) => ({ ...prev, title: '' }));
                    }
                  }}
                  className="w-full border rounded-md p-2 mt-1"
                />
                {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
              </div>

              <div>
                <label className="text-[16px] font-semibold text-[#62556B]">Description</label>
                <Textarea
                  disabled={!isEditing}
                  value={form?.description ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm({ ...form!, description: value });

                    const result = categorySchema.safeParse({
                      ...form,
                      description: value,
                    });

                    if (!result.success) {
                      const fieldError = result.error.errors.find(
                        (err) => err.path[0] === 'description'
                      );

                      setErrors((prev) => ({
                        ...prev,
                        description: fieldError?.message || '',
                      }));
                    } else {
                      setErrors((prev) => ({ ...prev, description: '' }));
                    }
                  }}
                  className="w-full border rounded-md p-2 mt-1"
                />
                {errors.description && (
                  <p className="text-red-500 text-sm mt-1">{errors.description}</p>
                )}
              </div>

              {errors.general && <p className="text-red-500 text-sm">{errors.general}</p>}
              {isEditing && (
                <Button
                  className="text-white bg-darkPurple hover:bg-lightPurple"
                  disabled={!isFormValid || isLoading}
                  onClick={handleSave}
                >
                  Save Changes
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-8 col-span-2">
            <div className="flex  bg-cardPurple text-white  rounded-xl shadow-md p-6 border-[1px] border-[#4B00821F]/20 transition-all duration-500 cursor-pointer  hover:-translate-y-2 hover:shadow-2xl">
              <div>
                <p className="text-sm opacity-80">Total Content</p>
                <h2 className="text-[40px] font-semibold mt-2">{totalContent.toLocaleString()}</h2>
              </div>
              <div className=" xl:ml-10">
                <img src="/playbtn.png" alt="" className=" w-15 h-15 opacity-10" />
              </div>
            </div>
            <div className="grid grid-cols-1  xl:gap-6 gap-4">
              <div className=" bg-white  rounded-xl px-2 lg:px-6 pt-8 pb-10 shadow-md border-[1px] border-[#4B00821F]/12 transition-all duration-500 cursor-pointer  hover:-translate-y-2 hover:shadow-2xl">
                <div className="flex justify-between ">
                  <p className="text-sm text-gray-400">Total Plays</p>
                  <img src="/clock.png" className=" w-10 h-10" alt="" />
                </div>
                <div>
                  <h3 className="text-2xl font-medium ">{totalPlays.toLocaleString()}</h3>
                </div>
              </div>
              {/* 
              <div className="bg-white rounded-xl shadow-md border-[1px] border-[#4B00821F]/12 px-4 lg:px-6 py-8 transition-all duration-500 cursor-pointer  hover:-translate-y-2 hover:shadow-2xl ">
                <div className="flex justify-between ">
                  <p className="text-sm text-gray-400">Total Users</p>
                  <img src="/admin.png" className=" w-15 h-10" alt="" />
                </div>
                <div>
                  <h3 className="text-2xl font-medium ">{totalUsers.toLocaleString()}</h3>
                </div>
              </div> */}
            </div>
          </div>
        </div>
        <div className="w-full shadow-[0px_4px_20px_0px_rgba(75,0,130,0.08)] rounded-xl bg-white border border-[#4B00821F]/10 overflow-hidden">
          <UnLockedTable />
        </div>
      </div>
    </>
  );
}
